import {html, LitElement} from 'lit';
import {customElement, state, property} from 'lit/decorators.js';
import {TariffFormStore} from './../Storage/tariffStorage';

import './FormElements/swsTariffCard';
import './FormElements/swsTariffDetails';
import {formatNumber, getLocale, getLocallangText} from '../swsRateCalculatorForm-init.mjs';

interface Tariff {
    title: string;
    title_long: string;
    media: string;
    zips: number[];
    icon: string;
    information_text: string;
    description_short: string;
    description: string;
    description_long: string;
    labor_price: number;
    base_price: any[];
    type: number;
    quantity_cap: number | null;
    faqs: {
        title: string,
        answer: string
    }
}

interface SelectedTariff {
    type: number;
    tariff: Tariff;
}

@customElement('sws-available-tariffs')
export class swsAvailableTariffs extends LitElement {

    // Define the properties of the component
    @property({type: String})
    zipCode = '';

    @property({type: Number})
    customerType = 0;

    @property({type: Array})
    products: Tariff[] = [];

    @property({type: Array})
    settings = [];

    // Define the product types 0
    @property({type: Boolean})
    productType_0 = false;

    @property({type: String})
    productType_0_yearly = '';

    @property({type: String})
    productType_0_space = '';

    // Define the product types 1
    @property({type: Boolean})
    productType_1 = false;

    @property({type: String})
    productType_1_yearly = '';

    @property({type: String})
    productType_1_space = '';

    // Define the product types 2
    @property({type: Boolean})
    productType_2 = false;

    @property({type: String})
    productType_2_yearly = '';

    @property({type: String})
    productType_2_people = '';

    @property({type: Boolean})
    productType_2_chargeCar = false;

    @property({type: Array}) selectedTariffs: Tariff[] = [];

    @property({type: Array}) allTariffs: Tariff[] = [];

    @property({type: String})
    cityName = '';

    @property({type: Array})
    seals: Array<{src: string; alt: string; title: string; link?: string}> = [];

    connectedCallback() {
        super.connectedCallback();

        // Vorhandene Listener …
        window.addEventListener('tariff-clear-tabs', this.handleClearSelectedTariffs.bind(this));
    }

    public updateElement() {
        this.requestUpdate();
    }

    private handleClearSelectedTariffs(e: CustomEvent) {
        this.selectedTariffs = [];
        this.requestUpdate();

        window.dispatchEvent(new CustomEvent('tariffs-current', {
            detail: []
        }));
        this._dispatchTariffs(this.selectedTariffs)
    }


    tariffGetBasePrice(tariff: Tariff, consumption: number): number {
        if (tariff.base_price.length > 1) {
            let base_price = 0;
            for (let i = 0; i < tariff.base_price.length; i++) {
                if (tariff.base_price[i]['consumption_level'] != null && consumption >= tariff.base_price[i]['consumption_level']) {
                    base_price = tariff.base_price[i]['base_price'];
                }
            }
            return base_price;
        } else {
            return tariff.base_price[0]['base_price'];
        }
    }

    calculateBasicProviderCostRaw(product: any): number {
        const consumption = this._getConsumptionGeneral({ type: parseInt(product.type) } as Tariff);
        const laborPrice = parseFloat(product.provider_labor_price ?? 0);
        const basePrice = parseFloat(product.provider_basic_price ?? 0);


        if (consumption === 0 || (laborPrice === 0 && basePrice === 0)) {
            return 0;
        }

        const result = ((laborPrice / 100 * consumption) + basePrice) / 12;
        return result;
    }

    calculateTariffCostsRaw(tariff: Tariff): number {
        let consumption = 0;
        let working_price = 0;
        let base_price = 0;

        switch (tariff.type) {
            case 0:
                consumption = (this.productType_0_yearly && parseInt(this.productType_0_yearly) > 0)
                    ? parseInt(this.productType_0_yearly)
                    : (this.settings['pricing']['areaGas'][this.productType_0_space] ?? 0);
                base_price = this.tariffGetBasePrice(tariff, consumption);
                working_price = tariff.labor_price / 100;
                break;
            case 1:
                consumption = (this.productType_1_yearly && parseInt(this.productType_1_yearly) > 0)
                    ? parseInt(this.productType_1_yearly)
                    : (this.settings['pricing']['areaHeatPump'][this.productType_1_space] ?? 0);
                base_price = this.tariffGetBasePrice(tariff, consumption);
                working_price = tariff.labor_price / 100;
                break;
            case 2:
                if (this.productType_2_yearly && parseInt(this.productType_2_yearly) > 0) {
                    consumption = parseInt(this.productType_2_yearly);
                } else {
                    consumption = this.productType_2_chargeCar
                        ? (this.settings['pricing']['persons']['chargesCar'][this.productType_2_people] ?? 0)
                        : (this.settings['pricing']['persons']['default'][this.productType_2_people] ?? 0);
                }
                base_price = this.tariffGetBasePrice(tariff, consumption);
                working_price = tariff.labor_price / 100;
                break;
        }

        const result = this.tariffCalculationEachMonth(consumption, working_price, base_price);
        return result;
    }

    getLowestTariffCost(tariffs: Tariff[]): number {
        if (tariffs.length === 0) return 0;

        const costs = tariffs
            .map(t => this.calculateTariffCostsRaw(t))
            .filter(c => c > 0);

        return costs.length > 0 ? Math.min(...costs) : 0;
    }

    calculateSavings(product: any, tariffs: Tariff[]): string {
        const allowedZips: string[] = product.provider_zip_codes ?? [];
        if (allowedZips.length > 0 && !allowedZips.includes(this.zipCode)) {
            return '';
        }

        const basicCost = this.calculateBasicProviderCostRaw(product);
        const lowestTariffCost = this.getLowestTariffCost(tariffs);

        if (basicCost === 0 || lowestTariffCost === 0) {
            return '';
        }

        const savings = Math.round((basicCost - lowestTariffCost) * 12);
        if (savings <= 0) {
            return '';
        }

        return savings.toLocaleString(getLocale(), {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        });
    }

    tariffCalculationEachMonth(consumption: number, working_price: number, base_price: number): number {
        if (consumption == 0 || base_price == 0) {
            return 0;
        }

        if (this.customerType == 1) {
            return ((working_price * consumption) + +base_price) / 12;
        } else {
            return ((working_price * consumption) + +base_price) / 12;
        }
    }

    calculateTariffCosts(tariff: Tariff): number {
        let consumption = 0;
        let working_price = 0;
        let base_price = 0;

        switch (tariff.type) {
            case 0:
                if (this.productType_0_yearly && parseInt(this.productType_0_yearly) > 0) {
                    consumption = parseInt(this.productType_0_yearly);
                } else {
                    consumption = this.settings['pricing']['areaGas'][this.productType_0_space] ? this.settings['pricing']['areaGas'][this.productType_0_space] : 0;
                }

                base_price = this.tariffGetBasePrice(tariff, consumption);
                working_price = tariff.labor_price / 100;
                break;
            case 1:
                if (this.productType_1_yearly && parseInt(this.productType_1_yearly) > 0) {
                    consumption = parseInt(this.productType_1_yearly);
                } else {
                    consumption = this.settings['pricing']['areaHeatPump'][this.productType_1_space] ? this.settings['pricing']['areaHeatPump'][this.productType_1_space] : 0;
                }

                base_price = this.tariffGetBasePrice(tariff, consumption);
                working_price = tariff.labor_price / 100;
                break;
            case 2:
                if (this.productType_2_yearly && parseInt(this.productType_2_yearly) > 0) {
                    consumption = parseInt(this.productType_2_yearly);
                } else {
                    if (this.productType_2_chargeCar) {
                        consumption = this.settings['pricing']['persons']['chargesCar'][this.productType_2_people] ? this.settings['pricing']['persons']['chargesCar'][this.productType_2_people] : 0;
                    } else {
                        consumption = this.settings['pricing']['persons']['default'][this.productType_2_people] ? this.settings['pricing']['persons']['default'][this.productType_2_people] : 0;
                    }
                }

                base_price = this.tariffGetBasePrice(tariff, consumption);
                working_price = tariff.labor_price / 100;
                break;
            default:
                break;
        }

        const tariffCalculationEachMonth = this.tariffCalculationEachMonth(consumption, working_price, base_price);

        return Math.round(tariffCalculationEachMonth);
    }

    tariffsForZipCode(zipCode: number): Tariff[] {
        const tariffs: Tariff[] = [];

        this.products.forEach(
            (product: any) => {
                if (product.type == 0 && this.productType_0 || product.type == 1 && this.productType_1 || product.type == 2 && this.productType_2) {
                    product.tariffs.forEach(
                        (tariff: Tariff) => {
                            if (tariff.zips.length > 0) {
                                tariff.zips.forEach(
                                    zip => {
                                        if (zipCode == zip) {
                                            tariffs.push(tariff);
                                        }
                                    }
                                )
                            } else {
                                tariffs.push(tariff);
                            }
                        }
                    )
                }
            }
        )

        return tariffs;
    }

    private _getConsumptionGeneral(tariff: Tariff): number {
        let consumption = 0;

        switch (tariff.type) {
            case 0:
                if (this.productType_0_yearly && parseInt(this.productType_0_yearly) > 0) {
                    consumption = parseInt(this.productType_0_yearly);
                } else {
                    consumption = this.settings['pricing']['areaGas'][this.productType_0_space] || 0;
                }
                break;
            case 1:
                if (this.productType_1_yearly && parseInt(this.productType_1_yearly) > 0) {
                    consumption = parseInt(this.productType_1_yearly);
                } else {
                    consumption = this.settings['pricing']['areaHeatPump'][this.productType_1_space] || 0;
                }
                break;
            case 2:
                if (this.productType_2_yearly && parseInt(this.productType_2_yearly) > 0) {
                    consumption = parseInt(this.productType_2_yearly);
                } else {
                    if (this.productType_2_chargeCar) {
                        consumption = this.settings['pricing']['persons']['chargesCar'][this.productType_2_people] || 0;
                    } else {
                        consumption = this.settings['pricing']['persons']['default'][this.productType_2_people] || 0;
                    }
                }
                break;
        }

        return consumption;
    }

    getTariffColClass(count: number): string {
        if (count >= 4) return 'col-12 col-sm-6 col-md-4 col-lg-3';
        if (count === 3) return 'col-12 col-sm-6 col-md-4';
        if (count === 2) return 'col-12 col-sm-6 col-md-4';
        return 'col-12 col-sm-6 col-md-4';
    }

    getActiveCategories(): number[] {
        const active: number[] = [];
        if (this.productType_2) active.push(2);
        if (this.productType_1) active.push(1);
        if (this.productType_0) active.push(0);
        return active;
    }

    private _selectTariff(e: CustomEvent) {
        const selected = e.detail as Tariff;

        const consumptionGeneral = this._getConsumptionGeneral(selected);

        let consumption = 0;
        let space = '';
        let people = '';
        let chargeCar = false;

        // Verbrauchsparameter je nach Tariftyp
        switch (selected.type) {
            case 0:
                if (this.productType_0_yearly && parseInt(this.productType_0_yearly) > 0) {
                    consumption = parseInt(this.productType_0_yearly);
                }
                space = this.productType_0_space;
                break;
            case 1:
                if (this.productType_1_yearly && parseInt(this.productType_1_yearly) > 0) {
                    consumption = parseInt(this.productType_1_yearly);
                }
                space = this.productType_1_space;
                break;
            case 2:
                if (this.productType_2_yearly && parseInt(this.productType_2_yearly) > 0) {
                    consumption = parseInt(this.productType_2_yearly);
                }
                people = this.productType_2_people;
                chargeCar = this.productType_2_chargeCar;
                break;
        }

        let updated = this.selectedTariffs.filter(t => t.type !== selected.type);

        const selectedWithMeta: Tariff = {
            ...selected,
            meta: {
                customerType: this.customerType,
                yearlyConsumption: consumption,
                space,
                people,
                chargeCar
            },
            consumptionGeneral: consumptionGeneral
        };

        this.selectedTariffs = [...updated, { type: selected.type, tariff: selectedWithMeta }];

        const activeTypes = this.getActiveCategories();
        const selectedTypes = this.selectedTariffs.map(t => t.type);
        const finalizedTariffs = [...this.selectedTariffs];

        window.dispatchEvent(new CustomEvent('tariffs-current', {
            detail: finalizedTariffs
        }));

        this._dispatchTariffs(finalizedTariffs)

        if (activeTypes.length == finalizedTariffs.length) {

            const deliveryTabButton = document.getElementById('delivery-tab');
            if (deliveryTabButton) {
                document.getElementById('tariff-tab')?.classList.add('ready', 'op-100');
                deliveryTabButton.removeAttribute('disabled');
                deliveryTabButton.click();
            }
        } else {
            const missingType = activeTypes.find(type => !selectedTypes.includes(type));
            if (missingType !== undefined) {
                this.activeCategory = missingType;
            }
            window.scrollTo({
                top: 0,
                behavior: 'smooth' // Optional: weich scrollen
            });
        }
    }

    private _dispatchTariffs(tariffs) {
        window.dispatchEvent(new CustomEvent('tariffs-current', {
            detail: tariffs
        }));
    }


    @property({type: Number}) activeCategory: number = 2;

    firstUpdated() {
        const trigger = this.renderRoot.querySelector('[data-bs-toggle="modal"][data-bs-target="#modal-change-tariff"]');
        if (trigger) {
            trigger.addEventListener('click', (e) => {
                e.preventDefault();

                const modalEl = document.getElementById('modal-change-tariff'); // 👉 das hat bei dir gefehlt
                const Modal = (window as any).bootstrap?.Modal;

                if (Modal && modalEl) {
                    const modal = new Modal(modalEl);
                    modal.show();
                } else {
                    console.warn('Bootstrap.Modal oder modalEl ist nicht verfügbar');
                }
            });
        }
    }

    protected updated(changedProperties: Map<string, unknown>) {
        super.updated(changedProperties);

        if (changedProperties.has('productType_0') || changedProperties.has('productType_1') || changedProperties.has('productType_2')) {
            const activeCategories = this.getActiveCategories();

            if (activeCategories.length > 0) {
                this.activeCategory = activeCategories[0];
            }
        }
    }

    @property({type: Array})
    chosenTariffs: SelectedTariff[] = [];

    hasHighlightedTariff(tariffs: Tariff[]): boolean {
        return tariffs.some((t: any) => t.highlight == 1);
    }

    sortTariffsWithHighlight(tariffs: Tariff[]): Tariff[] {
        return [...tariffs].sort((a: any, b: any) => {
            if (b.highlight == 1) return 1;
            if (a.highlight == 1) return -1;
            return 0;
        });
    }

    protected render() {

        window.addEventListener('city-name-updated', (event: CustomEvent) => {
            const cityName = event.detail;

            this.cityName = cityName;
            this.requestUpdate();
        });

        if( !this.productType_0 && !this.productType_1 && !this.productType_2 ) {
            return html``;
        }

        const tariffs = this.tariffsForZipCode(parseInt(this.zipCode));
        const activeCategories = this.getActiveCategories();
        const activeLabel = this.getCategoryLabel(this.activeCategory);
        const index = this.getIndex(this.activeCategory);

        const activeTypes = this.getActiveCategories();

        const selectedTypes = this.selectedTariffs.map(t => t.type);

        const remainingTypes = activeTypes.filter(type => !selectedTypes.includes(type));
        const isLastType = remainingTypes.length === 1 && remainingTypes[0] === this.activeCategory;

        const filteredTariffs = this.sortTariffsWithHighlight(
            tariffs.filter(t => t.type === this.activeCategory)
        );
        const filteredColClass = this.getTariffColClass(filteredTariffs.length);

        if (tariffs.length == 0) {
            if (this.zipCode) {
                document.getElementById('sws-rateCalculator-chooseTariffPage')?.classList.remove('d-none');
                document.getElementById('sws-rateCalculator-configureTariffPage')?.classList.add('d-none');
                document.getElementById('chooseTariff-noTariffFound')?.classList.remove('d-none');
                document.getElementById('chooseTariff-noTariffFound')!.focus();
            }
            return html``;
        } else if (activeCategories.length >= 1) {

            const missingTariffs = [];
            activeCategories.forEach(function(value) {
                const hasTariffsForActiveCategory = tariffs.some(t => t.type === value);
                if (!hasTariffsForActiveCategory) {
                    missingTariffs.push(value);
                }
            });

            if(missingTariffs.length > 0) {
                document.getElementById('chooseTariff-noTariffFound')?.classList.add('d-none');
                document.getElementById('chooseTariff-missing-0')?.classList.add('d-none');
                document.getElementById('chooseTariff-missing-0-1')?.classList.add('d-none');
                document.getElementById('chooseTariff-missing-0-2')?.classList.add('d-none');
                document.getElementById('chooseTariff-missing-1')?.classList.add('d-none');
                document.getElementById('chooseTariff-missing-1-2')?.classList.add('d-none');
                document.getElementById('chooseTariff-missing-2')?.classList.add('d-none');

                document.getElementById('sws-rateCalculator-chooseTariffPage')?.classList.remove('d-none');
                document.getElementById('sws-rateCalculator-configureTariffPage')?.classList.add('d-none');
                let missingTypes = '';
                missingTariffs.forEach(function (value) {
                    missingTypes += '-' + value;
                });
                document.getElementById('chooseTariff-missing' + missingTypes)?.classList.remove('d-none');

                return html``;
            }

            document.getElementById('chooseTariff-noTariffFound')?.classList.add('d-none');
            document.getElementById('chooseTariff-missing-0')?.classList.add('d-none');
            document.getElementById('chooseTariff-missing-0-1')?.classList.add('d-none');
            document.getElementById('chooseTariff-missing-0-2')?.classList.add('d-none');
            document.getElementById('chooseTariff-missing-1')?.classList.add('d-none');
            document.getElementById('chooseTariff-missing-1-2')?.classList.add('d-none');
            document.getElementById('chooseTariff-missing-2')?.classList.add('d-none');


            return html`
                <div id="product-container" class="sws-available-tariffs">
                        ${activeCategories.length > 1
                                ? html`
                                    <div class="d-flex justify-content-center mt-md-4">
                                        <ul class="nav nav-tabs mb-3 mb-md-0">
                                            ${activeCategories.map(
                                                    category => html`
                                                        <li class="nav-item">
                                                            <button
                                                                    class="nav-link btn-toggle-bg font-18 py-3 px-4  ${this.activeCategory === category ? 'active' : ''}"
                                                                    @click="${() => (this.activeCategory = category)}" aria-label="${getLocallangText("form.chooseTariff.ariaLabel", [this.getCategoryLabel(category)])}"
                                                            >
                                                                ${this.getCategoryLabel(category)}
                                                            </button>
                                                        </li>
                                                    `
                                            )}
                                        </ul>
                                    </div>
                                `
                                : null}
                    <div class="p-lg-3-75 pb-2-375 text-center">
                        <h1 class="font-36 fw-bold">
                            ${getLocallangText("form.chooseTariff.header", [activeLabel])}</h1>
                        <div class="d-flex justify-content-center flex-column flex-wrap flex-md-row p-2-25 px-0 pt-0">
                            <p class="m-0 px-2">${TYPO3?.lang?.["form.chooseTariff.headerinfo.product"]}
                                <strong>${activeLabel}
                                    ${this.customerType == 0 ? html`${TYPO3?.lang?.["form.chooseTariff.headerinfo.customerType.private"]}` : html`${TYPO3?.lang?.["form.chooseTariff.headerinfo.customerType.commercial"]}`}</strong>
                            </p>
                            <p class="m-0 px-2">${TYPO3?.lang?.["form.chooseTariff.headerinfo.consumption.title"]}
                                <strong>${formatNumber(this._getConsumptionGeneral({ type: this.activeCategory } as Tariff))} kWh</strong></p>


                            <p class="m-0 px-2">${TYPO3?.lang?.["form.chooseTariff.headerinfo.zipCode.title"]}
                                <strong>${this.zipCode}
                                    ${this.cityName ? this.cityName : ''} </strong></p>

                        </div>
                        ${(() => {
                            const activeProduct = this.products.find((p: any) => parseInt(p.type) === this.activeCategory);
                            const savings = activeProduct ? this.calculateSavings(activeProduct, filteredTariffs) : '';
                            return savings ? html`
                                <div class="d-flex justify-content-center p-2-25 px-0 pt-0">
                                    <div class="bg-primary text-white rounded px-4 py-3 text-center">
                                        <p class="m-0">
                                            <strong>${getLocallangText("form.chooseTariff.headerinfo.basicProvider.savings", [savings])}*</strong>
                                        </p>
                                    </div>
                                </div>
                            ` : null;
                        })()}
                        <div class="row m-0 sws-tariff-cards ${this.hasHighlightedTariff(filteredTariffs) ? 'has-highlight mt-sm-5' : ''} ${filteredTariffs.length <= 2 ? 'justify-content-center' : 'justify-content-start'}">
                            ${filteredTariffs.map(
                                    (tariff: any, i) => {
                                        let orderClass = '';
                                        if ((tariff as any).highlight == 1) {
                                            orderClass = 'order-first order-md-2';
                                        } else {
                                            // Nicht-highlighted Tarife: aufsteigend, aber Lücke bei md-2 lassen
                                            const nonHighlightIndex = filteredTariffs
                                                    .filter((t: any) => t.highlight != 1)
                                                    .indexOf(tariff);
                                            orderClass = nonHighlightIndex === 0 ? 'order-md-1' : 'order-md-3';
                                        }
                                        return html`
                                            <div class="${filteredColClass} p-0 px-1-5 mb-5 ${orderClass}">
                                                <sws-tariff-card
                                                        .tariff="${{
                                                            ...tariff,
                                                            consumption: this._getConsumptionGeneral(tariff),
                                                            zipCode: this.zipCode,
                                                            price: this.calculateTariffCosts(tariff),
                                                            activeLabel: activeLabel
                                                        }}"
                                                        colClass="${filteredColClass}"
                                                        @show-tariff-details="${this._showTariffDetails}"
                                                        @tariff-selected="${this._selectTariff}"
                                                ></sws-tariff-card>
                                            </div>
                                        `
                                    }
                            )}
                        </div>
                    </div>
                    <div class="p-lg-3-75 pt-0 pb-2-375">
                        <p class="text-center text-gray-light-8 line-height-150">
                           ${this.products[index].information_text}
                        </p>
                        ${(() => {
                            const activeProduct = this.products.find((p: any) => parseInt(p.type) === this.activeCategory);
                            const savings = activeProduct ? this.calculateSavings(activeProduct, filteredTariffs) : '';
                            return savings ? html`
                                <p class="text-center text-gray-light-8 line-height-150">
                                    *${TYPO3?.lang?.["form.chooseTariff.headerinfo.basicProvider.savings.infoText"]}
                                </p>
                            ` : null;
                        })()}
                    </div>

                    ${this.seals?.length ? html`
                        <div class="sws-rateCalculator-seals row row-cols-2 row-cols-md-4 g-seals mb-5 px-lg-3-75">
                            ${this.seals.map(seal => html`
                                <div class="col d-flex justify-content-center align-items-center">
                                    ${seal.link
                                        ? html`<a href="${seal.link}" class="d-inline-block">
                                                  <img src="${seal.src}" alt="${seal.alt ?? ''}" title="${seal.title ?? ''}" class="img-fluid" loading="lazy" />
                                               </a>`
                                        : html`<img src="${seal.src}" alt="${seal.alt ?? ''}" title="${seal.title ?? ''}" class="img-fluid" loading="lazy" />`
                                    }
                                </div>
                            `)}
                        </div>
                    ` : html``}

                    ${this.productType_0 || this.productType_1 || this.productType_2
                            ? html`
                                <sws-tariff-details
                                        .tariffs="${tariffs.filter(t => t.type === this.activeCategory).map(t => ({
                                            ...t,
                                            consumption: this._getConsumptionGeneral(t),
                                            zipCode: this.zipCode,
                                            price: this.calculateTariffCosts(t),
                                            activeLabel: activeLabel
                                        }))}"
                                        @tariff-selected="${this._selectTariff}"
                                ></sws-tariff-details>
                            `
                            : html``}
                </div>
                </div>

            `;
        }
    }

    private _showTariffDetails(e: CustomEvent) {
        const title = e.detail.title;

        const detailComponent = this.querySelector('sws-tariff-details') as any;

        if (detailComponent) {
            detailComponent.setActiveTariffByTitle?.(title);

            detailComponent.scrollIntoView({behavior: 'smooth'});
        }
    }


    getCategoryLabel(type: number): string {
        switch (type) {
            case 2:
                return TYPO3?.lang?.["form.chooseTariff.type.2"];
            case 1:
                return TYPO3?.lang?.["form.chooseTariff.type.1"];
            case 0:
                return TYPO3?.lang?.["form.chooseTariff.type.0"];
            default:
                return 'Tarif';
        }
    }
    getIndex(type: number): number {
        switch (type) {
            case 2:
                return 0;
            case 1:
                return 1;
            case 0:
                return 2;
            default:
                return 0;
        }
    }

    protected createRenderRoot() {
        return this;
    }
}

